import { Button, Grid, SvgIcon, Typography } from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { ChartCard } from '../chart-card';
import { HourColumns, RankBar, ShareBar } from '../charts';
import { TrendPanel } from '../trend-panel';
import { ReportTable } from '../report-table';
import { inr, num } from '../report-theme';
import { orderValueBuckets, ReportModel, revenueByHour } from '../report-data';

interface SalesTabProps {
	report: ReportModel;
	hasAdvancedAnalytics: boolean;
	onViewAllOrders: () => void;
}

export const SalesTab = ({ report, hasAdvancedAnalytics, onViewAllOrders }: SalesTabProps) => {
	const hours = revenueByHour(report);
	const buckets = orderValueBuckets(report).filter((bucket) => bucket.count > 0);
	const hasHourData = hours.some((bucket) => bucket.revenue > 0);

	return (
		<Grid container spacing={3}>
			{hasAdvancedAnalytics && (
				<Grid item xs={12}>
					<TrendPanel report={report} height={330} />
				</Grid>
			)}

			<Grid item xs={12} md={5}>
				<ChartCard title="Payment methods" subtitle="Cash handled versus digital collections">
					<ShareBar
						slices={[
							{ label: 'Cash', value: report.paymentReport.cash },
							{ label: 'Card', value: report.paymentReport.card },
							{ label: 'UPI', value: report.paymentReport.upi },
						]}
					/>
				</ChartCard>
			</Grid>

			<Grid item xs={12} md={7}>
				<ChartCard
					title="Ticket size"
					subtitle="How many orders fall in each value band"
					isEmpty={buckets.length === 0}
					emptyMessage="No orders in the selected period."
				>
					<RankBar
						categories={buckets.map((bucket) => bucket.label)}
						values={buckets.map((bucket) => bucket.count)}
						formatter={(value) => `${num(value)} order${value === 1 ? '' : 's'}`}
						axisFormatter={(value) => num(value)}
						height={Math.max(180, buckets.length * 42 + 40)}
					/>
				</ChartCard>
			</Grid>

			<Grid item xs={12}>
				<ChartCard
					title="Busiest hours"
					subtitle="Revenue by hour of day — the peak hour is highlighted"
					isEmpty={!hasHourData}
					emptyMessage="No timed orders in the selected period."
				>
					<HourColumns hours={hours} />
				</ChartCard>
			</Grid>

			<Grid item xs={12}>
				<ChartCard
					title="Recent orders"
					subtitle={`${num(report.orders.length)} order${report.orders.length === 1 ? '' : 's'} in this period`}
					action={
						report.orders.length > 0 ? (
							<Button
								color="inherit"
								size="small"
								endIcon={<SvgIcon><ArrowRightIcon /></SvgIcon>}
								onClick={onViewAllOrders}
							>
								View all
							</Button>
						) : undefined
					}
				>
					<ReportTable
						columns={[
							{ title: 'Order', render: (row: any) => row.sequenceNumber },
							{
								title: 'Customer',
								render: (row: any) =>
									row.customerName || (
										<Typography variant="body2" color="text.secondary">
											Walk-in
										</Typography>
									),
							},
							{ title: 'Payment', render: (row: any) => row.paymentMethod ?? '—' },
							{
								title: 'Date',
								render: (row: any) =>
									row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-IN') : '—',
							},
							{
								title: 'Total',
								align: 'right',
								render: (row: any) => (
									<Typography variant="body2" sx={{ fontWeight: 600 }}>
										{inr(row.total)}
									</Typography>
								),
							},
						]}
						rows={report.orders.slice(0, 8)}
						emptyMessage="No orders in the selected period."
					/>
				</ChartCard>
			</Grid>
		</Grid>
	);
};
